import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { FormsModule } from "@angular/forms";
import { CalendarModule } from "primeng/calendar";
import { firstValueFrom, forkJoin } from "rxjs";
import { ClubService } from "../../services/club.service";
import { CourtService } from "../../services/court.service";
import { CustomModalService } from "../../services/custom-modal.service";
import { CustomNotificationService } from "../../services/custom-notification.service";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";
import { UserService } from "../../services/user.service";
import { CourtReservation } from "../../model/court-reservation";

type SurfaceFilter = "all" | "indoor" | "outdoor";
type MatchFilter = "single" | "double";

interface Court {
  id: number;
  club_id: number;
  name: string;
  img_path: string;
  court_type: 0 | 1 | "Outdoor" | "Indoor";
  match_type_mask: 1 | 2 | 3;
  [key: string]: any;
}

interface Club {
  id: number;
  name: string;
  location: string;
  img_path: string;
  available_courts?: string;
  court?: Court[];
  number_of_courts?: number;
  clubOpeningExceptions?: any[];
  clubPricingBundles?: any[];
  clubPricingHours?: any[];
  clubOpeningHours?: any[];
  price_per_hour?: number;
  imageError?: boolean;
  [key: string]: any;
}

@Component({
  selector: "app-book-a-court",
  standalone: true,
  imports: [CommonModule, FormsModule, CalendarModule, LucideAngularModule],
  templateUrl: "./book-a-court.component.html",
  styleUrl: "./book-a-court.component.css",
})
export class BookACourtComponent implements OnInit {
  clubs: Club[] = [];
  filteredClubs: Club[] = [];
  private _filterType: SurfaceFilter = "all";
  get filterType(): SurfaceFilter {
    return this._filterType;
  }
  set filterType(type: SurfaceFilter) {
    this._filterType = type;
    this.applyClubFilter();
    this.applyCourtFilter();
  }
  matchType: MatchFilter = "double";
  courts: Court[] = [];
  availableCourts: { label: string; value: Court }[] = [];
  times: { hour: number; available: boolean }[] = [];
  clubReservations: any[] = [];
  selectedClub: Club | null = null;
  selectedCourt: Court | null = null;
  selectedReservation?: CourtReservation;
  isLoadingMatches = true;
  isLoadingClubs = true;
  isLoadingCourts = true;
  currentUser?: any;
  filteredCourts: Court[] = [];
  selectedTimes: number[] = [];
  reservationDate: Date | null = null;
  verified = true;
  vipForSelectedClub = false;
  isPrivate = false;
  opponentName = "";
  teammateName = "";
  opponent1Name = "";
  opponent2Name = "";
  currentUsername = "";
  clubWorkingHoursDisplay = "";
  clubMinPrice = 0;
  totalPrice = 0;
  showConfirmDialog = false;
  confirmMessage = "";
  clubInfoVisible = false;
  loading = false;
  dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  matchTypeOptions: {
    label: string;
    value: MatchFilter;
    disabled?: boolean;
  }[] = [
    { label: "Double", value: "double" },
    { label: "Single", value: "single", disabled: true },
  ];
  availableMatchTypeOptions = [...this.matchTypeOptions];
  singleSupported = false;
  minDate: Date = new Date();
  @ViewChild("clubDetails") clubDetails?: ElementRef<HTMLElement>;

  setFilterType(type: SurfaceFilter) {
    this.filterType = type;
  }

  onMatchTypeChange() {
    if (this.matchType === "single" && !this.singleSupported) {
      this.matchType = "double";
      return;
    }
    this.applyCourtFilter();
    this.computeTotalPrice();
  }

  private updateMatchTypeOptions() {
    this.availableMatchTypeOptions = this.matchTypeOptions.map((o) =>
      o.value === "single" ? { ...o, disabled: !this.singleSupported } : o,
    );
  }

  private matchesSurface(c: Court, sf: SurfaceFilter): boolean {
    if (sf === "all") return true;
    let type = c.court_type;
    if (type === "Indoor") type = 1;
    else if (type === "Outdoor") type = 0;
    return sf === "indoor" ? type === 1 : type === 0;
  }

  private matchesMatchType(c: Court, mt: MatchFilter): boolean {
    if (mt === "single") {
      return (c.match_type_mask & 1) !== 0;
    } else {
      return (c.match_type_mask & 2) !== 0;
    }
  }

  private clubHasCourtsFor(club: Club, sf: SurfaceFilter): boolean {
    if (sf === "all") return true;
    return (club.court ?? []).some((c) => this.matchesSurface(c, sf));
  }

  private applyClubFilter() {
    const currentUsername = this.auth.currentUserValue?.username?.toLowerCase();

    this.filteredClubs = (this.clubs ?? []).filter((club) => {
      if (
        club.name.toLowerCase().includes("padrinos") &&
        currentUsername !== "dimitrijek"
      ) {
        return false;
      }
      return this.clubHasCourtsFor(club, this.filterType);
    });

    if (
      this.selectedClub &&
      !this.clubHasCourtsFor(this.selectedClub, this.filterType)
    ) {
      this.selectedClub = null;
      this.courts = [];
      this.filteredCourts = [];
      this.availableCourts = [];
    }
  }

  private applyCourtFilter() {
    if (!this.selectedClub) return;
    const courts = this.courts.length
      ? this.courts
      : this.selectedClub.court ?? [];
    this.filteredCourts = courts.filter(
      (c) =>
        this.matchesSurface(c, this.filterType) &&
        this.matchesMatchType(c, this.matchType),
    );
    if (this.selectedClub.id === 8 && this.reservationDate) {
      const dow = this.reservationDate.getDay();
      if (dow >= 1 && dow <= 5) {
        this.filteredCourts = this.filteredCourts.filter(
          (c) => c.name === "Padel Bros court 1",
        );
      }
    }
    this.computeAvailableCourts();
  }

  constructor(
    private clubService: ClubService,
    private courtService: CourtService,
    private reservationService: CourtReservationService,
    private auth: AuthenticationService,
    private userService: UserService,
    private modalService: CustomModalService,
    private notificationService: CustomNotificationService,
  ) {}

  ngOnInit(): void {
    const current = this.auth.currentUserValue;
    if (!current) {
      this.verified = false;
      this.vipForSelectedClub = false;
      return;
    }
    this.currentUser = current;
    this.currentUsername = current.username;
    this.loading = true;
    this.userService.getUser(current.id).subscribe({
      next: (u) => {
        this.verified = u.email_verified;
        if (this.verified) {
          forkJoin({
            clubs: this.clubService.getClubs(),
            courts: this.courtService.getItems(),
          }).subscribe({
            next: ({ clubs, courts }) => {
              this.clubs = clubs as Club[];
              const grouped: { [key: number]: Court[] } = {};
              (courts as Court[]).forEach((c: Court) => {
                grouped[c.club_id] = grouped[c.club_id] || [];
                grouped[c.club_id].push(c);
              });
              this.clubs.forEach((club) => {
                club.court = grouped[club.id] || [];
              });
              this.applyClubFilter();
              this.loading = false;
            },
            error: () => {
              this.loading = false;
            },
          });
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.verified = false;
        this.vipForSelectedClub = false;
        this.loading = false;
      },
    });
  }

  async selectClub(club: Club) {
    const currentUsername = this.auth.currentUserValue?.username?.toLowerCase();

    if (
      club.name.toLowerCase().includes("padrinos padel") &&
      currentUsername !== "dimitrijek"
    ) {
      await this.modalService.info({
        title: "Coming Soon",
        message:
          "This club is currently in demo mode. Booking functionality will be available soon!",
        confirmText: "OK",
      });
      return;
    }

    this.selectedClub = club;
    this.selectedCourt = null;
    this.selectedTimes = [];
    this.times = [];
    this.reservationDate = null;
    this.clubReservations = [];
    this.courts = [];
    this.filteredCourts = [];
    this.availableCourts = [];
    this.totalPrice = 0;
    this.matchType = "double";
    this.singleSupported = false;
    this.updateMatchTypeOptions();

    this.loading = true;
    try {
      const userId = this.auth.currentUserValue?.id;
      if (!userId) return;
      const vip = await firstValueFrom(this.clubService.getVipUsers(club.id));
      this.vipForSelectedClub = vip.some((u: any) => u.id === userId);
      this.courts = club.court ?? [];
      this.singleSupported = this.courts.some(
        (c) => (c.match_type_mask & 1) !== 0,
      );
      this.updateMatchTypeOptions();
      this.matchType = "double";
      this.applyCourtFilter();
    } finally {
      this.loading = false;
    }

    this.computeClubInfo();
    setTimeout(() => {
      const element = this.clubDetails?.nativeElement;
      if (!element) return;

      let scrollContainer = element.parentElement;
      while (scrollContainer && scrollContainer !== document.body) {
        const style = window.getComputedStyle(scrollContainer);
        const overflowY = style.overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
          break;
        }
        scrollContainer = scrollContainer.parentElement;
      }

      if (scrollContainer && scrollContainer !== document.body) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;
        const targetScroll =
          scrollTop + elementRect.top - containerRect.top - 20;

        scrollContainer.scrollTo({
          top: targetScroll,
          behavior: "smooth",
        });
      } else {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 100);
  }

  openClubInfo() {
    this.clubInfoVisible = true;
  }

  onDateChange() {
    this.selectedTimes = [];
    this.availableCourts = [];
    this.times = [];
    this.clubReservations = [];
    if (this.selectedClub && this.reservationDate) {
      this.loading = true;
      const local = new Date(
        this.reservationDate.getTime() -
          this.reservationDate.getTimezoneOffset() * 60000,
      );
      const dateStr = local.toISOString().split("T")[0];
      this.reservationService
        .getReservationsForClub(this.selectedClub.id, dateStr)
        .subscribe({
          next: (res) => {
            this.clubReservations = res;
            this.applyCourtFilter();
            this.generateAvailableTimes();
            this.computeTotalPrice();
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        });
    }
  }

  private generateAvailableTimes() {
    if (!this.reservationDate) return;
    const intervals = this.computeOpeningIntervals(this.reservationDate);
    const now = new Date();
    const isToday =
      this.reservationDate.getFullYear() === now.getFullYear() &&
      this.reservationDate.getMonth() === now.getMonth() &&
      this.reservationDate.getDate() === now.getDate();
    for (const interval of intervals) {
      const start = Math.ceil(interval.start);
      const end = Math.floor(interval.end);
      for (let i = start; i < end; i++) {
        const reservationsAtHour = this.clubReservations.filter((r) => {
          const s = new Date(r.reservation_start_time).getHours();
          const e = new Date(r.reservation_end_time).getHours();
          return i >= s && i < e;
        });
        const reservedCourts = new Set(
          reservationsAtHour.map((r: any) => r.court_id),
        );
        let available =
          reservedCourts.size < (this.selectedClub!.number_of_courts ?? 0);
        if (isToday && i <= now.getHours()) {
          available = false;
        }
        this.times.push({ hour: i, available });
      }
    }
  }

  selectTime(hour: number) {
    const time = this.times.find((t) => t.hour === hour);
    if (!time?.available) {
      return;
    }
    if (this.selectedTimes.includes(hour)) {
      this.selectedTimes = this.selectedTimes.filter((h) => h !== hour);
    } else {
      this.selectedTimes.push(hour);
      this.selectedTimes.sort((a, b) => a - b);
    }
    this.computeAvailableCourts();
    this.computeTotalPrice();
  }

  private computeAvailableCourts() {
    if (this.selectedTimes.length === 0) {
      this.availableCourts = this.filteredCourts.map((c) => ({
        label: c.name,
        value: c,
      }));
      return;
    }
    const reservedCourtIds = new Set(
      this.clubReservations
        .filter((r) => {
          const s = new Date(r.reservation_start_time).getHours();
          const e = new Date(r.reservation_end_time).getHours();
          return this.selectedTimes.some((t) => t >= s && t < e);
        })
        .map((r: any) => r.court_id),
    );
    this.availableCourts = this.filteredCourts
      .filter((c) => !reservedCourtIds.has(c.id))
      .map((c) => ({ label: c.name, value: c }));
    if (
      !this.availableCourts.find((c) => c.value.id === this.selectedCourt?.id)
    ) {
      this.selectedCourt = null;
    }
  }

  private getHour(ts: any): number {
    if (!ts) return 0;
    if (typeof ts === "string") {
      const parts = ts.split(":");
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      return h + m / 60;
    }
    if (typeof ts === "object" && "hours" in ts) {
      return ts.hours + (ts.minutes || 0) / 60;
    }
    const num = Number(ts);
    return isNaN(num) ? 0 : num;
  }

  private computeOpeningIntervals(
    date: Date,
    club: any = this.selectedClub,
  ): { start: number; end: number }[] {
    if (!club) return [];
    const dow = date.getDay();
    let intervals = (club.clubOpeningHours || [])
      .filter((o: any) => o.day_of_week === dow)
      .map((o: any) => ({
        start: this.getHour(o.start_time),
        end: this.getHour(o.end_time),
      }));
    const exc = (club.clubOpeningExceptions || []).find((e: any) => {
      const d = new Date(e.date_);
      return (
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
      );
    });
    if (exc) {
      if (exc.is_closed) return [];
      const excStart = this.getHour(exc.start_time);
      const excEnd = this.getHour(exc.end_time);
      intervals = intervals.flatMap((int: any) => {
        if (excEnd <= int.start || excStart >= int.end) return [int];
        const res: { start: number; end: number }[] = [];
        if (excStart > int.start)
          res.push({ start: int.start, end: Math.min(excStart, int.end) });
        if (excEnd < int.end)
          res.push({ start: Math.max(excEnd, int.start), end: int.end });
        return res;
      });
    }
    return intervals;
  }

  private formatHour(h: number): string {
    return Math.floor(h).toString().padStart(2, "0") + ":00";
  }

  private formatIntervals(intervals: { start: number; end: number }[]): string {
    return intervals
      .map((i) => `${this.formatHour(i.start)}-${this.formatHour(i.end)}`)
      .join(", ");
  }

  private computeClubInfo() {
    if (!this.selectedClub) {
      this.clubWorkingHoursDisplay = "";
      this.clubMinPrice = 0;
      return;
    }
    const today = new Date();
    const intervals = this.computeOpeningIntervals(today);
    this.clubWorkingHoursDisplay = intervals.length
      ? this.formatIntervals(intervals)
      : "Closed";
    this.clubMinPrice = this.getMinPrice(today);
  }

  getClubWorkingHoursDisplay(club: any): string {
    const today = new Date();
    const intervals = this.computeOpeningIntervals(today, club);
    return intervals.length ? this.formatIntervals(intervals) : "Closed";
  }

  getClubMinPrice(club: any): number {
    const today = new Date();
    return this.getMinPrice(today, club);
  }

  formatTime(ts: any): string {
    const h = this.getHour(ts);
    const hour = Math.floor(h);
    const min = Math.round((h - hour) * 60);
    return (
      hour.toString().padStart(2, "0") + ":" + min.toString().padStart(2, "0")
    );
  }

  private formatDayRange(days: number[]): string {
    if (days.length === 7) {
      return "Every day";
    }
    const sorted = [...days].sort((a, b) => a - b);
    const ranges: string[] = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const cur = sorted[i];
      if (cur === prev + 1) {
        prev = cur;
        continue;
      }
      ranges.push(
        start === prev
          ? this.dayNames[start]
          : `${this.dayNames[start]}-${this.dayNames[prev]}`,
      );
      start = prev = cur;
    }
    ranges.push(
      start === prev
        ? this.dayNames[start]
        : `${this.dayNames[start]}-${this.dayNames[prev]}`,
    );
    return ranges.join(", ");
  }

  getAllOpeningHours(club: any): string[] {
    const groups: { [key: string]: number[] } = {};
    const daysWithHours = new Set<number>();
    for (const o of club.clubOpeningHours || []) {
      const interval = `${this.formatTime(o.start_time)}-${this.formatTime(
        o.end_time,
      )}`;
      groups[interval] = groups[interval] || [];
      groups[interval].push(o.day_of_week);
      daysWithHours.add(o.day_of_week);
    }
    const closed: number[] = [];
    for (let i = 0; i < 7; i++) {
      if (!daysWithHours.has(i)) closed.push(i);
    }
    if (closed.length) {
      groups["Closed"] = closed;
    }
    return Object.entries(groups).map(([interval, days]) =>
      interval === "Closed"
        ? `${this.formatDayRange(days)}: Closed`
        : `${this.formatDayRange(days)}: ${interval}`,
    );
  }

  getPricingHoursDisplay(club: any): string[] {
    const groups: { [key: string]: number[] } = {};
    for (const p of club.clubPricingHours || []) {
      const interval = `${this.formatTime(p.start_time)}-${this.formatTime(
        p.end_time,
      )}`;
      const key = `${interval}|${p.price_rsds}`;
      groups[key] = groups[key] || [];
      groups[key].push(p.day_of_week);
    }
    return Object.entries(groups).map(([key, days]) => {
      const [interval, price] = key.split("|");
      return `${this.formatDayRange(days)} ${interval}: ${price} RSD/h`;
    });
  }

  getBundleDisplay(club: any): string[] {
    const groups: { [key: string]: number[] } = {};
    for (const b of club.clubPricingBundles || []) {
      const timePart =
        b.start_time && b.end_time
          ? `${this.formatTime(b.start_time)}-${this.formatTime(b.end_time)}`
          : "";
      const key = `${b.bundle_hours}|${b.price_total_rsds}|${timePart}`;
      groups[key] = groups[key] || [];
      if (b.day_of_week == null) {
        groups[key] = [0, 1, 2, 3, 4, 5, 6];
      } else {
        groups[key].push(b.day_of_week);
      }
    }
    return Object.entries(groups).map(([key, days]) => {
      const [hours, price, time] = key.split("|");
      const dayStr = this.formatDayRange(days);
      const timeStr = time ? ` ${time}` : "";
      return `${dayStr} ${hours}h${timeStr} - ${price} RSD`;
    });
  }

  formatException(e: any): string {
    const date = new Date(e.date_).toLocaleDateString();
    if (e.is_closed) {
      return `${date}: Closed`;
    }
    return `${date}: ${this.formatTime(e.start_time)}-${this.formatTime(
      e.end_time,
    )}`;
  }

  private getMinPrice(date: Date, club: any = this.selectedClub): number {
    if (!club) return 0;
    const dow = date.getDay();
    const pricing = (club.clubPricingHours || []).filter(
      (p: any) => p.day_of_week === dow,
    );
    if (pricing.length) {
      return Math.min(...pricing.map((p: any) => p.price_rsds));
    }
    return club.price_per_hour;
  }

  private computeTotalPrice() {
    if (
      !this.selectedClub ||
      !this.reservationDate ||
      this.selectedTimes.length === 0
    ) {
      this.totalPrice = 0;
      return;
    }
    const groups = this.groupSelectedTimes();
    let total = 0;
    for (const g of groups) {
      const startHour = g[0];
      const duration = g.length;
      const start = new Date(this.reservationDate);
      start.setHours(startHour, 0, 0, 0);
      total += this.calculatePrice(start, duration);
    }
    this.totalPrice = total;
  }

  private calculatePrice(start: Date, duration: number): number {
    const dow = start.getDay();
    const t = start.getHours() + start.getMinutes() / 60;
    const pricing = (this.selectedClub!.clubPricingHours || []).find(
      (p: any) =>
        p.day_of_week === dow &&
        t >= this.getHour(p.start_time) &&
        t < this.getHour(p.end_time),
    );
    const hourly = pricing
      ? pricing.price_rsds
      : this.selectedClub!.price_per_hour;
    const bundle = (this.selectedClub!.clubPricingBundles || []).find(
      (b: any) =>
        b.bundle_hours === duration &&
        (b.day_of_week == null || b.day_of_week === dow) &&
        (!b.start_time || t >= this.getHour(b.start_time)) &&
        (!b.end_time || t < this.getHour(b.end_time)),
    );
    const totalHourly = hourly * duration;
    return bundle
      ? Math.min(bundle.price_total_rsds, totalHourly)
      : totalHourly;
  }

  private sanitizeUsername(name: string): string {
    return name.trim().replace(/^@/, "").toLowerCase();
  }

  private hasAtLeastTwoPlayers(): boolean {
    if (this.matchType === "single") {
      return !!this.sanitizeUsername(this.opponentName);
    }
    const team = this.sanitizeUsername(this.teammateName);
    const opp1 = this.sanitizeUsername(this.opponent1Name);
    const opp2 = this.sanitizeUsername(this.opponent2Name);
    return !!(team || opp1 || opp2);
  }

  bookCourt() {
    if (
      !this.selectedCourt ||
      !this.reservationDate ||
      this.selectedTimes.length === 0
    )
      return;

    if (!this.hasAtLeastTwoPlayers()) {
      this.notificationService.warning(
        "Missing players",
        "Please add at least one more player",
      );
      return;
    }

    const reservationGroups = this.groupSelectedTimes();
    this.confirmMessage = `Book ${reservationGroups.length} reservation(s) on court ${this.selectedCourt.name}?`;
    this.showConfirmDialog = true;
  }

  private groupSelectedTimes(): number[][] {
    const times = [...this.selectedTimes].sort((a, b) => a - b);
    const groups: number[][] = [];
    let current: number[] = [];
    for (const t of times) {
      if (!current.length || t === current[current.length - 1] + 1) {
        current.push(t);
      } else {
        groups.push(current);
        current = [t];
      }
    }
    if (current.length) groups.push(current);
    return groups;
  }

  private async performBooking() {
    for (const t of this.selectedTimes) {
      const start = new Date(this.reservationDate!);
      start.setHours(t, 0, 0, 0);
      if (start < new Date()) {
        this.notificationService.warning(
          "Invalid time",
          "Cannot book a court in the past",
        );
        return;
      }
    }

    if (!this.hasAtLeastTwoPlayers()) {
      this.notificationService.warning(
        "Missing players",
        "Please add at least one more player",
      );
      return;
    }

    if (this.matchType === "single") {
      const opp = this.sanitizeUsername(this.opponentName);
      if (opp && opp === this.currentUsername.toLowerCase()) {
        this.notificationService.warning(
          "Invalid player",
          "You cannot play against yourself",
        );
        return;
      }
    }

    if (this.matchType === "double") {
      const team = this.sanitizeUsername(this.teammateName);
      const opp1 = this.sanitizeUsername(this.opponent1Name);
      const opp2 = this.sanitizeUsername(this.opponent2Name);
      const names = [this.currentUsername.toLowerCase()];
      if (team) names.push(team);
      if (opp1) names.push(opp1);
      if (opp2) names.push(opp2);
      if (new Set(names).size !== names.length) {
        this.notificationService.warning(
          "Invalid players",
          "Duplicate player usernames are not allowed",
        );
        return;
      }
    }

    const currentUser = this.auth.currentUserValue;
    if (!currentUser) return;

    const players = [
      {
        court_reservation_id: 0,
        player_id: currentUser.id,
        position: 0,
        player: {
          id: currentUser.id,
          full_name: `${currentUser.name} ${currentUser.last_name}`,
        },
      },
    ];
    this.loading = true;
    try {
      if (this.matchType === "single" && this.opponentName) {
        const name = this.opponentName.replace(/^@/, "");
        console.log("Looking up opponent:", name);
        const opponent = await firstValueFrom(
          this.userService.getUserByUsername(name),
        );
        console.log("Opponent result:", opponent);
        if (!opponent) {
          this.notificationService.warning(
            "User not found",
            `Player @${name} not found`,
          );
          this.loading = false;
          return;
        }
        players.push({
          court_reservation_id: 0,
          player_id: opponent.id,
          position: 3,
          player: {
            id: opponent.id,
            full_name: `${opponent.name} ${opponent.last_name}`,
          },
        });
      }

      if (this.matchType === "double") {
        const team = this.teammateName.replace(/^@/, "");
        const opp1 = this.opponent1Name.replace(/^@/, "");
        const opp2 = this.opponent2Name.replace(/^@/, "");

        const [teammate, opponent1, opponent2] = await Promise.all([
          team
            ? firstValueFrom(this.userService.getUserByUsername(team))
            : Promise.resolve(null),
          opp1
            ? firstValueFrom(this.userService.getUserByUsername(opp1))
            : Promise.resolve(null),
          opp2
            ? firstValueFrom(this.userService.getUserByUsername(opp2))
            : Promise.resolve(null),
        ]);

        if (team && !teammate) {
          this.notificationService.warning(
            "User not found",
            `Player @${team} not found`,
          );
          this.loading = false;
          return;
        }
        if (opp1 && !opponent1) {
          this.notificationService.warning(
            "User not found",
            `Player @${opp1} not found`,
          );
          this.loading = false;
          return;
        }
        if (opp2 && !opponent2) {
          this.notificationService.warning(
            "User not found",
            `Player @${opp2} not found`,
          );
          this.loading = false;
          return;
        }

        if (teammate) {
          players.push({
            court_reservation_id: 0,
            player_id: teammate.id,
            position: 1,
            player: {
              id: teammate.id,
              full_name: `${teammate.name} ${teammate.last_name}`,
            },
          });
        }
        if (opponent1) {
          players.push({
            court_reservation_id: 0,
            player_id: opponent1.id,
            position: 3,
            player: {
              id: opponent1.id,
              full_name: `${opponent1.name} ${opponent1.last_name}`,
            },
          });
        }
        if (opponent2) {
          players.push({
            court_reservation_id: 0,
            player_id: opponent2.id,
            position: 4,
            player: {
              id: opponent2.id,
              full_name: `${opponent2.name} ${opponent2.last_name}`,
            },
          });
        }
      }

      const groups = this.groupSelectedTimes();
      for (const g of groups) {
        const startHour = g[0];
        const duration = g.length;
        const start = new Date(this.reservationDate!);
        start.setHours(startHour, 0, 0, 0);
        const startLocal = new Date(
          start.getTime() - start.getTimezoneOffset() * 60000,
        );
        const endLocal = new Date(
          startLocal.getTime() + duration * 60 * 60 * 1000,
        );

        const body = {
          club_id: this.selectedClub!.id,
          court_id: this.selectedCourt!.id,
          user_id: this.auth.currentUserValue?.id,
          reservation_start_time: startLocal.toISOString(),
          reservation_end_time: endLocal.toISOString(),
          started: false,
          finished: false,
          reservation_status: this.vipForSelectedClub ? "Approved" : "Pending",
          is_private: this.isPrivate,
          match_type: this.matchType,
          sets: [-1, -1, -1, -1, -1, -1],
          reservation_type: "non employee",
          courtReservationPlayer: players,
        };
        await firstValueFrom(
          this.reservationService.addCourtReservation(body as any),
        );
      }

      this.notificationService.success(
        "Success",
        this.vipForSelectedClub
          ? "Reservation booked"
          : "Reservation pending approval",
      );
      this.isPrivate = false;
      this.selectedCourt = null;
      this.selectedTimes = [];
      this.reservationDate = null;
      this.times = [];
      this.availableCourts = [];
      this.opponentName = "";
      this.teammateName = "";
      this.opponent1Name = "";
      this.opponent2Name = "";
    } catch (error) {
      console.error("Failed to book court:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      this.notificationService.error("Error", "Failed to book court");
    } finally {
      this.loading = false;
    }
  }

  openMaps(location: string) {
    const url =
      "https://www.google.com/maps/dir/?api=1&destination=" +
      encodeURIComponent(location);
    window.open(url, "_blank");
  }

  handleImageError(event: any) {
    const imgElement = event.target;
    const clubCard = imgElement.closest(".club-card");
    if (clubCard) {
      const clubIndex = Array.from(clubCard.parentElement.children).indexOf(
        clubCard,
      );
      if (this.filteredClubs[clubIndex]) {
        this.filteredClubs[clubIndex].imageError = true;
      }
    }
  }

  handleUserImageError(event: any) {
    if (this.currentUser) {
      this.currentUser.imageError = true;
    }
  }

  selectCourt(court: Court) {
    this.selectedCourt = court;
  }

  closeClubInfo() {
    this.clubInfoVisible = false;
  }

  cancelBooking() {
    this.showConfirmDialog = false;
  }

  confirmBooking() {
    this.showConfirmDialog = false;
    this.proceedWithBooking();
  }

  private proceedWithBooking() {
    this.performBooking();
  }
}
